import { Box, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BriaButton from "../../../../../../components/common/BriaButton/BriaButton.tsx";
import BriaTooltip from "../../../../../../components/common/BriaTooltip/BriaTooltip.tsx";
import { useAppStore } from "../../../../../../hooks/useStores.tsx";
import border from "../../../../../assets/svgs/border-style-2.svg";
import { PanelType } from "../../../../../constants/app-options.ts";
import { DesignEditorContext } from "../../../../../contexts/DesignEditor.tsx";
import { getFillColor } from "../../../../../core/utils/gradient_color_utils.ts";
import { useActiveObject } from "../../../../../hooks/useActiveObject";
import useAppContext from "../../../../../hooks/useAppContext";
import { useEditor } from "../../../../../hooks/useEditor.tsx";
import useSetIsSidebarOpen from "../../../../../hooks/useSetIsSidebarOpen.tsx";
import { LayerType } from "../../../../../types/layers.ts";
import EditAllAdsSwitch from "../../Panels/panelItems/Common/EditAllAdsSwitch/EditAllAdsSwitch";
import FabricElementsShadow from "../../Panels/panelItems/Common/FabricElementsShadow/FabricElementsShadow.tsx";
import FabricObjectsProperties from "../../Panels/panelItems/Common/FabricObjectsProperties/FabricObjectsProperties.tsx";
import BorderPopover from "./BorderPopover/BorderPopover.tsx";
import styles from "./FabricShapes.module.scss";
const FabricShapes = () => {
	const editor = useEditor();
	const { t } = useTranslation("translation", { keyPrefix: "editor.tabs" });
	const activeObject = useActiveObject() as any;
	const { setActivePanel } = useAppContext();
	const [strokeColor, setStrokeColor] = useState(activeObject?.stroke);
	const [fillColor, setFillColor] = useState(getFillColor(activeObject?.fill));

	const [hideStrokeColor, setHideStrokeColor] = useState<boolean>(!activeObject?.strokeWidth);
	const setIsSidebarOpen = useSetIsSidebarOpen();
	const { isPopupView } = useContext(DesignEditorContext);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement>();
	const { uiStore } = useAppStore();
	const isFabricShape =
		activeObject?.type === LayerType.STATIC_RECT ||
		activeObject?.type === LayerType.STATIC_CIRCLE ||
		activeObject?.type === LayerType.STATIC_TRIANGLE;

	useEffect(() => {
		let watcher = async () => {
			if (activeObject && isFabricShape) {
				setStrokeColor(activeObject.stroke);
				setFillColor(getFillColor(activeObject.fill));

				setHideStrokeColor(!activeObject.strokeWidth);
			}
		};
		if (editor) {
			editor.on("history:changed", watcher);
		}
		return () => {
			if (editor) {
				editor.off("history:changed", watcher);
			}
		};
	}, [editor, activeObject]);
	const handleOpenDialog = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};
	const handleClose = () => {
		setAnchorEl(undefined);
	};

	return (
		<Box
			className={clsx({
				[styles.container]: true,
				[styles.hide]: isPopupView,
			})}
		>
			<Box
				className={clsx({
					[styles.shapesToolBar]: true,
					[styles.noMarginRight]: uiStore.showGuideSection,
				})}
			>
				<Box className={styles.rightSideControls}>
					<Box className={styles.leftToolBarSide}>
						<BriaTooltip title={t("fillColor")}>
							<Box className={styles.shapeColors}>
								<Box
									style={{
										backgroundColor: fillColor,
										background: fillColor,
									}}
									onClick={() => {
										setIsSidebarOpen(true);
										setActivePanel(PanelType.SHAPEFILLCOLOR);
									}}
									className={styles.colorBox}
								></Box>
							</Box>
						</BriaTooltip>
						{!hideStrokeColor ? (
							<BriaTooltip title={t("borderColor")}>
								<Box className={styles.shapeColors}>
									<Box
										style={{
											backgroundColor: strokeColor,
											background: strokeColor,
										}}
										onClick={() => {
											setIsSidebarOpen(true);
											setActivePanel(PanelType.STROKECOLOR);
										}}
										className={styles.colorBox}
									></Box>
								</Box>
							</BriaTooltip>
						) : null}
					</Box>
					<Box className={styles.borderVerticalDivider} />
					<Box>
						<BriaButton
							buttonType="textMedium"
							onClick={handleOpenDialog}
							className={clsx({
								[styles.BorderButtonWrapper]: true,
								[styles.selected]: !!anchorEl,
							})}
						>
							<img src={border} />
							<Typography className={styles.buttonLabel}>{t("border")}</Typography>
						</BriaButton>
						<BorderPopover open={!!anchorEl} handleClose={handleClose} anchorEl={anchorEl} />
					</Box>
					<Box className={styles.verticalDivider} />
					<FabricElementsShadow />
					<Box className={styles.verticalDivider} />
					<FabricObjectsProperties />
				</Box>
				<EditAllAdsSwitch />
			</Box>
		</Box>
	);
};

const ObservedComponent = observer(FabricShapes);
export default ObservedComponent;
